import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { API_URL } from '../config';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'theme-preference';

export const ThemeProvider = ({ children }) => {
  const { currentUser, authFetch } = useAuth();

  // User's selected preference ('light', 'dark', 'auto')
  const [userPreference, setUserPreference] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || 'auto';
  });

  // System/OS preference (from prefers-color-scheme)
  const [systemPreference, setSystemPreference] = useState(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Effective mode computed from user + system preferences
  const effectiveMode = useMemo(() => {
    if (userPreference === 'auto') {
      return systemPreference;
    }
    return userPreference;
  }, [userPreference, systemPreference]);

  // Listen to system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      setSystemPreference(e.matches ? 'dark' : 'light');
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Legacy browsers
    else {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Sync with user profile when logged in
  useEffect(() => {
    if (currentUser?.theme_preference) {
      setUserPreference(currentUser.theme_preference);
      localStorage.setItem(STORAGE_KEY, currentUser.theme_preference);
    }
  }, [currentUser]);

  // Set theme preference (saves to backend if authenticated)
  const setThemeMode = async (mode) => {
    if (!['light', 'dark', 'auto'].includes(mode)) {
      console.error('Invalid theme mode:', mode);
      return;
    }

    // Update local state immediately for responsive UI
    setUserPreference(mode);
    localStorage.setItem(STORAGE_KEY, mode);

    // Sync to backend if authenticated
    if (currentUser && authFetch) {
      try {
        await authFetch(`${API_URL}/api/auth/me/theme`, {
          method: 'PUT',
          body: JSON.stringify({ theme_preference: mode }),
        });
      } catch (error) {
        console.error('Failed to save theme preference:', error);
      }
    }
  };

  // Toggle between modes (light → dark → auto → light)
  const toggleTheme = () => {
    const nextMode = {
      'light': 'dark',
      'dark': 'auto',
      'auto': 'light',
    }[userPreference];

    setThemeMode(nextMode);
  };

  const value = {
    userPreference,
    systemPreference,
    effectiveMode,
    setThemeMode,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
